import ApiClient, { PublicApiClient } from 'src/api/client';
import {
  AccountsRoutes,
  AdminRoutes,
  ArticlesRoutes,
  CompanyRoutes,
  DistributorRoutes,
  InvoicesRoutes,
  LandingRoutes,
  ListsRoutes,
  OfferCatalogRoutes,
  OrdersRoutes,
  PermissionsRoutes,
  ProductsRoutes,
  StatisticsRoutes,
  SupplierRoutes,
  UserRoutes,
  ValuationsRoutes,
  WarehouseRoutes
} from 'src/api/routes';

export class AccountsApi {
  static postLogin(data) {
    return ApiClient.post(AccountsRoutes.LOGIN, data);
  }
  static postRegister(data) {
    return ApiClient.post(AccountsRoutes.REGISTER, data);
  }
  static validateInvitation(data) {
    return ApiClient.get(AccountsRoutes.VALIDATE_INVITATION, data);
  }
  static getUserData() {
    return ApiClient.get(AccountsRoutes.GET_USER_DATA);
  }
  static forgotPassword(data) {
    return ApiClient.post(AccountsRoutes.FORGOT_PASSWORD, data);
  }
  static resetPassword(data) {
    return ApiClient.post(AccountsRoutes.RESET_PASSWORD, data);
  }
  static joinCompany(data) {
    return ApiClient.post(AccountsRoutes.JOIN_COMPANY, data);
  }
  static changePassword(data) {
    return ApiClient.post(AccountsRoutes.ACCOUNT_CHANGE_PASSWORD, data);
  }
  static getAttachment(params) {
    return ApiClient.get(AccountsRoutes.GET_ATTACHMENT, params, {
      responseType: 'blob'
    });
  }
  static getShipAndPayMethods(params) {
    return ApiClient.get(AccountsRoutes.GET_SHIP_AND_PAY_METHODS, params);
  }
}

export class CompanyApi {
  static updateCompanyData(data) {
    return ApiClient.post(CompanyRoutes.UPDATE_COMPANY, data);
  }
  static createNewCompany(data) {
    return ApiClient.post(CompanyRoutes.CREATE_NEW_COMPANY, data);
  }
  static getAllCompanies() {
    return ApiClient.get(CompanyRoutes.GET_ALL_COMPANIES);
  }
  static updateShippingDetails(data) {
    return ApiClient.put(CompanyRoutes.ADD_SHIPPING_ADDRESS, data);
  }
  static getMembers() {
    return ApiClient.get(CompanyRoutes.GET_USERS);
  }
  static removeMember(data) {
    return ApiClient.post(CompanyRoutes.REMOVE_USER, data);
  }
  static getInvitesList() {
    return ApiClient.get(CompanyRoutes.GET_INVITES_LIST);
  }
  static deleteShippingDetails(id) {
    return ApiClient.delete(CompanyRoutes.DELETE_SHIPPING_DETAILS(id));
  }
  static deleteCompanyMember(data) {
    return ApiClient.post(CompanyRoutes.DELETE_COMPANY_MEMBER, data);
  }
  static cancelInvitation(data) {
    return ApiClient.post(CompanyRoutes.CANCEL_INVITATION, data);
  }
  static saveLogo(data) {
    return ApiClient.post(CompanyRoutes.SAVE_LOGO, data);
  }
  static getProductsWithSuppliers(params, signal) {
    return ApiClient.get(CompanyRoutes.COLLECTION_GET, params, { signal });
  }
  static addSupplierToProduct(data) {
    return ApiClient.post(CompanyRoutes.ADD_SUPPLIER_TO_PRODUCT, data);
  }
  static removeSupplierFromProduct(data) {
    return ApiClient.post(CompanyRoutes.REMOVE_SUPPLIER_FROM_PRODUCT, data);
  }
  static getThresholds(params) {
    return ApiClient.get(CompanyRoutes.GET_THRESHOLDS, params);
  }
  static addThreshold(params) {
    return ApiClient.post(CompanyRoutes.ADD_THRESHOLDS, params);
  }
  static deleteThreshold(id) {
    return ApiClient.delete(CompanyRoutes.DELETE_THRESHOLDS, { id });
  }
  static editThreshold(data) {
    return ApiClient.put(CompanyRoutes.EDIT_THRESHOLD, data);
  }
  static getEmployees(params) {
    return ApiClient.get(CompanyRoutes.GET_EMPLOYEES, params);
  }
  static createEmployee(data) {
    return ApiClient.post(CompanyRoutes.CREATE_EMPLOYEE, data);
  }
  static editCompanyUser(data) {
    return ApiClient.post(CompanyRoutes.EDIT_COMPANY_USER, data);
  }
  static addEmployee(data) {
    return ApiClient.post(CompanyRoutes.ADD_EMPLOYEE, data);
  }
  static sendWelcomeMessage(id) {
    return ApiClient.post(CompanyRoutes.SEND_WELCOME_MESSAGE(id));
  }
  static getUsersValuationsCreate(params) {
    return ApiClient.get(CompanyRoutes.GET_USERS_VALUATIONS_CREATE, params);
  }
  static addUserToCompany(data, signal) {
    return ApiClient.post(CompanyRoutes.ADD_USER_TO_COMPANY, data, { signal });
  }
}

export class UserApi {
  static me() {
    return ApiClient.get(UserRoutes.ME);
  }
  static getDistributors(params) {
    return ApiClient.get(UserRoutes.GET_DISTRIBUTORS, params);
  }
  static addSupplier(data) {
    return ApiClient.post(UserRoutes.ADD_SUPPLIER, data);
  }
  static editSupplier(data) {
    return ApiClient.post(UserRoutes.EDIT_SUPPLIER, data);
  }
  static deleteSupplier({ id }) {
    return ApiClient.delete(UserRoutes.DELETE_SUPPLIER(id));
  }
  static getProducts(params, signal) {
    return ApiClient.get(UserRoutes.GET_USER_PRODUCTS, params, { signal });
  }
  static getProductCreationData(params) {
    return ApiClient.get(UserRoutes.GET_USER_PROD_CREATION_DATA, params);
  }
  static addProduct(params) {
    return ApiClient.put(UserRoutes.ADD_PRODUCT, params);
  }
  static saveUserData(data) {
    return ApiClient.put(UserRoutes.ME, data);
  }
  static deleteShippingDetails(id) {
    return ApiClient.delete(UserRoutes.DELETE_SHIPPING_DETAILS(id));
  }
  static updateInvoiceDetails(data) {
    return ApiClient.put(UserRoutes.UPDATE_INVOICE_DETAILS, data);
  }
  static addProducer(name) {
    return ApiClient.put(UserRoutes.ADD_PRODUCER, { name });
  }
  static getNotifications(params, signal) {
    return ApiClient.get(UserRoutes.GET_NOTIFICATIONS, params, { signal });
  }
  static getUnreadNotifications(params, signal) {
    return ApiClient.get(UserRoutes.GET_UNREAD_NOTIFICATIONS, params, { signal });
  }
  static setNotificationAsRead(id) {
    return ApiClient.post(UserRoutes.SET_NOTIFICATION_AS_READ, { id });
  }
  static setAllNotificationsAsRead() {
    return ApiClient.post(UserRoutes.SET_ALL_NOTIFICATIONS_AS_READ);
  }
  static updateUserData(data) {
    return ApiClient.put(UserRoutes.UPDATE_USER_DATA, data);
  }
  static saveDashboardConfig(data) {
    return ApiClient.put(UserRoutes.SAVE_DASHBOARD_CONFIG, { dashboard_config: data });
  }
}

export class ListsApi {
  static getLists(params, signal) {
    return ApiClient.get(ListsRoutes.GET_LISTS, params, { signal });
  }
  static createList(data) {
    return ApiClient.post(ListsRoutes.CREATE_LIST, data);
  }
  static mergeLists(data) {
    return ApiClient.post(ListsRoutes.MERGE_LISTS, data);
  }
  static editListName(data) {
    return ApiClient.post(ListsRoutes.EDIT_LIST_NAME, data);
  }
  static getListProducts(uid, signal) {
    return ApiClient.post(ListsRoutes.GET_LIST_PRODUCTS, { uid }, { signal });
  }
  static storeToList(data, signal) {
    return ApiClient.post(ListsRoutes.STORE_PRODUCT_TO_LIST, data, { signal });
  }
  static deleteProduct({ uid, product_id }) {
    return ApiClient.delete(ListsRoutes.DELETE_PRODUCT(uid, product_id));
  }
  static deleteList(uid) {
    return ApiClient.delete(ListsRoutes.DELETE_LIST(uid));
  }
  static createPricing(data) {
    return ApiClient.post(ListsRoutes.CREATE_PRICING, data);
  }
  static createHorizontalPricing(data) {
    return ApiClient.post(ListsRoutes.CREATE_HORIZONTAL_PRICING, data);
  }
  static ExportCSV(data) {
    return ApiClient.get(ListsRoutes.EXPORT_CSV, data);
  }
  static ListDuplicate(data) {
    return ApiClient.post(ListsRoutes.LIST_DUPLICATE, data);
  }
  static saveListComment(data, signal) {
    return ApiClient.post(ListsRoutes.SAVE_LIST_COMMENT, data, { signal });
  }
  static setProductsComment(data) {
    return ApiClient.post(ListsRoutes.SET_PRODUCTS_COMMENT, data);
  }
  static getData(data, signal) {
    return ApiClient.get(ListsRoutes.GET_CART_DATA, data, { signal });
  }
  static getSummary(params, signal) {
    return ApiClient.get(ListsRoutes.GET_CART_SUMMARY, params, { signal });
  }
  static removeAllSelects(data, signal) {
    return ApiClient.post(ListsRoutes.REMOVE_ALL_SELECTS, data, { signal });
  }
  static optimizeOfferCatalog(data) {
    return ApiClient.post(ListsRoutes.OPTIMIZE_OFFER_CATALOG, data);
  }
  static selectPreferred(data, signal) {
    return ApiClient.post(ListsRoutes.SELECT_PREFERRED, data, { signal });
  }
  static setPreferredSuppliers(data, signal) {
    return ApiClient.post(ListsRoutes.SET_PREFERRED_SUPPLIERS, data, { signal });
  }
  static selectPrice(data) {
    return ApiClient.post(ListsRoutes.SELECT_PRICE, data);
  }
  static resolveOrderData(params, signal) {
    return ApiClient.get(ListsRoutes.RESOLVE_ORDER_DATA, params, { signal });
  }
  static createOrder(data) {
    return ApiClient.post(ListsRoutes.CREATE_ORDER, data);
  }
  static getListActions(list_uid) {
    return ApiClient.get(ListsRoutes.GET_LIST_ACTIONS, { list_uid });
  }
  static getRecentlyOrderedProducts(list_uid) {
    return ApiClient.get(ListsRoutes.GET_RECENTLY_ORDERED_PRODUCTS, { list_uid });
  }
}

export class InvoicesApi {
  static getInvoices(params, signal) {
    return ApiClient.get(InvoicesRoutes.GET_INVOICES, params, { signal });
  }
  static addUserInvoice(data) {
    return ApiClient.post(InvoicesRoutes.ADD_USER_INVOICE, data);
  }
  static addUserInvoiceOther(data) {
    return ApiClient.post(InvoicesRoutes.ADD_USER_INVOICE_OTHER, data);
  }
  static editUserInvoice(data) {
    return ApiClient.post(InvoicesRoutes.EDIT_USER_INVOICE, data);
  }
  static editUserInvoiceOther(data) {
    return ApiClient.post(InvoicesRoutes.EDIT_USER_INVOICE_OTHER, data);
  }
  static downloadInvoiceOther(params) {
    return ApiClient.get(InvoicesRoutes.DOWNLOAD_INVOICE_OTHER, params, {
      responseType: 'blob'
    });
  }
  static downloadInvoice(params) {
    return ApiClient.get(InvoicesRoutes.DOWNLOAD_INVOICE, params, {
      responseType: 'blob'
    });
  }
  static deleteUserInvoice(data) {
    return ApiClient.post(InvoicesRoutes.DELETE_USER_INVOICE, data);
  }
  static deleteUserInvoiceOther(data) {
    return ApiClient.post(InvoicesRoutes.DELETE_USER_INVOICE_OTHER, data);
  }
  static addSuppliersInvoice(data) {
    return ApiClient.post(InvoicesRoutes.ADD_SUPPLIERS_INVOICE, data);
  }
  static editSuppliersInvoice(data) {
    return ApiClient.post(InvoicesRoutes.EDIT_SUPPLIERS_INVOICE, data);
  }
  static deleteSupplierInvoice(data) {
    return ApiClient.post(InvoicesRoutes.DELETE_SUPPLIER_INVOICE, data);
  }
  static getActions(data) {
    return ApiClient.get(InvoicesRoutes.GET_ACTIONS, data);
  }
}

export class ProductsApi {
  static getProducts(params, signal) {
    return ApiClient.get(ProductsRoutes.GET_PRODUCTS, params, { signal });
  }
  static getProductsProperties(signal) {
    return ApiClient.get(ProductsRoutes.GET_PRODUCTS_PROPERTIES, {}, { signal });
  }
  static getProductCreationData(params) {
    return ApiClient.get(ProductsRoutes.GET_PROD_CREATION_DATA, params);
  }
  static addProduct(params) {
    return ApiClient.put(ProductsRoutes.ADD_PRODUCT, params);
  }
  static deleteProduct(product_id) {
    return ApiClient.delete(ProductsRoutes.DELETE_PRODUCT(product_id));
  }
  static getProducers(params, signal) {
    return ApiClient.get(ProductsRoutes.GET_PRODUCERS, params, { signal });
  }
  static getCategories(params, signal) {
    return ApiClient.get(ProductsRoutes.GET_CATEGORIES, params, { signal });
  }
  static productDetails(id, params) {
    return ApiClient.get(ProductsRoutes.PRODUCT_DETAILS(id), params);
  }
  static productOrders(id, params) {
    return ApiClient.get(ProductsRoutes.PRODUCT_ORDERS(id), params);
  }
  static productPricings(id, params) {
    return ApiClient.get(ProductsRoutes.PRODUCT_PRICINGS(id), params);
  }
  static exportAllProducts(params) {
    return ApiClient.get(ProductsRoutes.EXPORT_ALL_PRODUCTS, params);
  }
  static importEditedProducts(data) {
    return ApiClient.post(ProductsRoutes.IMPORT_EDITED_PRODUCTS, data);
  }
  static replaceProduct(data) {
    return ApiClient.post(ProductsRoutes.REPLACE_PRODUCT, data);
  }
}

export class OfferCatalogApi {
  static getData(data, signal) {
    return ApiClient.get(OfferCatalogRoutes.GET_DATA, data, { signal });
  }
  static selectPrice(data) {
    return ApiClient.post(OfferCatalogRoutes.SELECT_PRICE, data);
  }
  static resolveOrderData(params, signal) {
    return ApiClient.get(OfferCatalogRoutes.RESOLVE_ORDER_DATA, params, { signal });
  }
  static changeQuantity(data) {
    return ApiClient.post(OfferCatalogRoutes.CHANGE_QUANTITY, data);
  }
  static addPrice(data) {
    return ApiClient.post(OfferCatalogRoutes.ADD_PRICE, data);
  }
  static createOrder(data) {
    return ApiClient.post(OfferCatalogRoutes.CREATE_ORDER, data);
  }
  static removeAllSelects(data, signal) {
    return ApiClient.post(OfferCatalogRoutes.REMOVE_ALL_SELECTS, data, { signal });
  }
  static getSummary(params, signal) {
    return ApiClient.get(OfferCatalogRoutes.GET_SUMMARY, params, { signal });
  }
  static optimizeOfferCatalog(data) {
    return ApiClient.post(OfferCatalogRoutes.OPTIMIZE_OFFER_CATALOG, data);
  }
  static setPreferredSupplier(data) {
    return ApiClient.post(OfferCatalogRoutes.SET_PREFERRED_SUPPLIER, data);
  }
  static deleteProduct(data) {
    return ApiClient.delete(OfferCatalogRoutes.DELETE_PRODUCT, data);
  }
  static deleteSinglePrice(data) {
    return ApiClient.post(OfferCatalogRoutes.DELETE_PRICE, data);
  }
  static selectPreferred(data, signal) {
    return ApiClient.post(OfferCatalogRoutes.SELECT_PREFERRED, data, { signal });
  }
  static exportCatalogCSV(data) {
    return ApiClient.get(OfferCatalogRoutes.EXPORT_CATALOG, data);
  }
  static exportCatalogXLSX(data) {
    return ApiClient.get(OfferCatalogRoutes.EXPORT_CATALOG, data);
  }
  static exportAllProductsForCatalog(data) {
    return ApiClient.get(OfferCatalogRoutes.EXPORT_ALL_PRODUCTS_FOR_CATALOG, data);
  }
  static importCatalogPrices(data) {
    return ApiClient.post(OfferCatalogRoutes.IMPORT_CATALOG_PRICES, data);
  }
  static getExpensesByOrder(data) {
    return ApiClient.post(OfferCatalogRoutes.GET_EXPENSES_BY_ORDER, data);
  }
  static getProducts(params, signal) {
    return ApiClient.get(OfferCatalogRoutes.GET_PRODUCTS, params, { signal });
  }
  static setPreferredSuppliers(data, signal) {
    return ApiClient.post(OfferCatalogRoutes.SET_PREFERRED_SUPPLIERS, data, { signal });
  }
  static getOfferCatalogProductsWithoutPrice(params) {
    return ApiClient.get(OfferCatalogRoutes.GET_OFFER_CATALOG_PRODUCTS_WITHOUT_PRICE, params);
  }
  static editPriceComment(data) {
    return ApiClient.post(OfferCatalogRoutes.EDIT_PRICE_COMMENT, data);
  }
  static deletePriceComment(priceId) {
    return ApiClient.delete(OfferCatalogRoutes.DELETE_PRICE_COMMENT(priceId));
  }
}

export class ValuationsApi {
  static getValuations(params, signal) {
    return ApiClient.get(ValuationsRoutes.GET_VALUATIONS, params, { signal });
  }
  static getValuationProducts(data, signal) {
    return ApiClient.get(ValuationsRoutes.GET_VALUATION_PRODUCTS, data, {
      signal
    });
  }
  static getSimpleValuationProducts(data) {
    return ApiClient.get(ValuationsRoutes.GET_VALUATION_PRODUCTS_SIMPLE, data);
  }
  static createOrder(data) {
    return ApiClient.post(ValuationsRoutes.CREATE_ORDER, data);
  }
  static saveOrderDataDistributor(data) {
    return ApiClient.post(ValuationsRoutes.SAVE_ORDER_DIST, data);
  }
  static saveOrderDataProducts(uid) {
    return ApiClient.post(ValuationsRoutes.SAVE_ORDER_PRODUCTS, { uid });
  }
  static resolveOrderData(data) {
    return ApiClient.get(ValuationsRoutes.RESOLVE_ORDER, data);
  }
  static cancelValuation(uid) {
    return ApiClient.delete(ValuationsRoutes.CANCEL_VALUATION(uid));
  }
  static ExportCSV(data) {
    return ApiClient.get(ValuationsRoutes.EXPORT_CSV, data);
  }
  static getProductDetails(data) {
    return ApiClient.get(ValuationsRoutes.GET_PRODUCT_DETAILS, data);
  }
  static changeDentametrPrice(data) {
    return ApiClient.post(ValuationsRoutes.CHANGE_DENTAMETR_PRICE, data);
  }
  static changeSelectedProductDistributor(data) {
    return ApiClient.post(ValuationsRoutes.CHANGE_SELECTED_PRODUCT_DIST, data);
  }
  static changeSupplierOffer(data) {
    return ApiClient.post(ValuationsRoutes.CHANGE_SUPPLIER_OFFER, data);
  }
  static deleteSelection(data) {
    return ApiClient.post(ValuationsRoutes.DELETE_OFFER_SELECTION, data);
  }
  static selectAllInColumn(data) {
    return ApiClient.post(ValuationsRoutes.SELECT_ALL_IN_COLUMN, data);
  }
  static unselectAllInColumn(data) {
    return ApiClient.post(ValuationsRoutes.UNSELECT_ALL_IN_COLUMN, data);
  }
  static selectSubstitute(data) {
    return ApiClient.post(ValuationsRoutes.SELECT_SUBSTITUTE, data);
  }
  static createSupplierValuation(data) {
    return ApiClient.post(ValuationsRoutes.CREATE_SUPPLIER_VALUATION, data);
  }
  static optimizeSelect(data) {
    return ApiClient.get(ValuationsRoutes.OPTIMIZE_SELECT, data);
  }
  static hideColumns(data) {
    return ApiClient.post(ValuationsRoutes.HIDE_COLUMNS, data);
  }
  static showColumns(data) {
    return ApiClient.post(ValuationsRoutes.SHOW_COLUMNS, data);
  }
  static changeProductQuantity(data) {
    return ApiClient.post(ValuationsRoutes.CHANGE_ORDER_QUANTITY, data);
  }
  static getPendingStatus(data) {
    return ApiClient.get(ValuationsRoutes.GET_PENDING_STATUS, data);
  }
}

export class OrdersApi {
  static getOrders(params, signal) {
    return ApiClient.get(OrdersRoutes.GET_ORDERS, params, { signal });
  }
  static getProducts(params, signal) {
    return ApiClient.get(OrdersRoutes.GET_PRODUCTS, params, { signal });
  }
  static cancelOrder(params) {
    return ApiClient.post(OrdersRoutes.CANCEL_ORDER, params);
  }
  static productReceived(data) {
    return ApiClient.post(OrdersRoutes.ORDER_PRODUCT_RECEIVED, data);
  }
  static downloadInvoice(params) {
    return ApiClient.get(OrdersRoutes.DOWNLOAD_INVOICE, params, {
      responseType: 'blob'
    });
  }
  static confirmProducts(data) {
    return ApiClient.post(OrdersRoutes.CONFIRM_PRODUCTS, data);
  }
  static changeProductsStatus(data) {
    return ApiClient.post(OrdersRoutes.CHANGE_PRODUCTS_STATUS, data);
  }
  static addOrderToWarehouse(data) {
    return ApiClient.post(OrdersRoutes.ADD_ORDER_TO_WAREHOUSE, data);
  }
  static addProductComment(data) {
    return ApiClient.post(OrdersRoutes.ADD_PRODUCT_COMMENT, data);
  }
  static uploadUserAttachment(data) {
    return ApiClient.post(OrdersRoutes.UPLOAD_USER_ATTACHMENT, data);
  }
  static deleteUserAttachment(data) {
    return ApiClient.post(OrdersRoutes.DELETE_USER_ATTACHMENT, data);
  }
  static addOrderMessage(data) {
    return ApiClient.post(OrdersRoutes.ADD_MESSAGE, data);
  }
  static cancelOrderLink(data) {
    return ApiClient.post(OrdersRoutes.CANCEL_ORDER_LINK, data);
  }
  static sentToApproval(uid) {
    return ApiClient.post(OrdersRoutes.SEND_ORDER_APPROVAL, { uid });
  }
  static partialReceiveProducts(data) {
    return ApiClient.post(OrdersRoutes.PARTIAL_RECEIVE_PRODUCTS, data);
  }
}

export class SupplierApi {
  static getCollections(params) {
    return ApiClient.get(SupplierRoutes.GET_COLLECTIONS, params);
  }
  static getForm(params) {
    return ApiClient.get(SupplierRoutes.GET_FORM, params);
  }
  static savePrice(data, signal) {
    return ApiClient.post(SupplierRoutes.SAVE_PRICE, data, { signal });
  }
  static removeOfferOrReplacement(data, signal) {
    return ApiClient.post(SupplierRoutes.REMOVE_OFFER_OR_REPLACEMENT, data, { signal });
  }
  static saveFormData(params) {
    return ApiClient.put(SupplierRoutes.SAVE_FORM_DATA, params);
  }
  static getOrder(params) {
    return ApiClient.get(SupplierRoutes.GET_ORDER, params);
  }
  static saveOrderLink(data) {
    return ApiClient.post(SupplierRoutes.SAVE_ORDER_LINK, data);
  }
  static updateOrder(data) {
    return ApiClient.post(SupplierRoutes.UPDATE_ORDER, data);
  }
  static finalizeOrder(data) {
    return ApiClient.post(SupplierRoutes.FINALIZE_ORDER, data);
  }
  static sendValuation(data) {
    return ApiClient.post(SupplierRoutes.SEND_VALUATION, data);
  }
  static getValuations(data, signal) {
    return ApiClient.get(SupplierRoutes.GET_VALUATIONS, data, { signal });
  }
  static getOrders(data, signal) {
    return ApiClient.get(SupplierRoutes.GET_ORDERS, data, { signal });
  }
  static cancelOrder(data) {
    return ApiClient.post(SupplierRoutes.CANCEL_ORDER, data);
  }
  static generatePdf(data) {
    return ApiClient.get(SupplierRoutes.GENERATE_PDF, data);
  }
  static addShippingMethod(data) {
    return ApiClient.post(SupplierRoutes.ADD_SHIPPING_METHOD, data);
  }
  static deleteShippingMethod(id, uid) {
    return ApiClient.post(SupplierRoutes.DELETE_SHIPPING_METHOD, { id, uid });
  }
  static registerSupplierAccount(data) {
    return ApiClient.post(SupplierRoutes.REGISTER_SUPPLIER_ACCOUNT, data);
  }
  static validateRegisterToken(data) {
    return ApiClient.get(SupplierRoutes.VALIDATE_REGISTER_TOKEN, data);
  }
  static connectSupplierValuations(data) {
    return ApiClient.get(SupplierRoutes.CONNECT_SUPPLIER_VALUATIONS, data);
  }
  static acceptProductQuantity(data) {
    return ApiClient.post(SupplierRoutes.ACCEPT_NEW_QUANTITY, data);
  }

  static acceptNewPrice(data) {
    return ApiClient.post(SupplierRoutes.ACCEPT_NEW_PRICE, data);
  }
  static getProducts(params, signal) {
    return ApiClient.get(SupplierRoutes.GET_PRODUCTS, params, {
      signal
    });
  }
  static addProductToCollection(data) {
    return ApiClient.post(SupplierRoutes.ADD_TO_COLLECTION, data);
  }
  static removeProductFromCollection(data) {
    return ApiClient.post(SupplierRoutes.REMOVE_FROM_COLLECTION, data);
  }
  static getClients(params, signal) {
    return ApiClient.get(SupplierRoutes.GET_CLIENTS, params, { signal });
  }
  static changePrice(data) {
    return ApiClient.post(SupplierRoutes.CHANGE_PRICE, data);
  }
  static uploadSupplierAttachment(data) {
    return ApiClient.post(SupplierRoutes.UPLOAD_SUPPLIER_ATTACHMENT, data);
  }
  static deleteSupplierAttachment(data) {
    return ApiClient.post(SupplierRoutes.DELETE_SUPPLIER_ATTACHMENT, data);
  }
  static addOrderMessage(data) {
    return ApiClient.post(SupplierRoutes.ADD_MESSAGE, data);
  }
  static orderApprovalData(token) {
    return ApiClient.get(SupplierRoutes.ORDER_APPROVAL_DATA, { token });
  }
  static orderApproval(data) {
    return ApiClient.post(SupplierRoutes.ORDER_APPROVAL, data);
  }
  static downloadInvoice(params) {
    return ApiClient.get(SupplierRoutes.DOWNLOAD_INVOICE, params, {
      responseType: 'blob'
    });
  }
}

export class DistributorApi {
  static getOrder(params, signal) {
    return ApiClient.get(DistributorRoutes.GET_ORDER, params, { signal });
  }
}

export class AdminApi {
  static UserProductToDb(data) {
    return ApiClient.post(AdminRoutes.USER_TO_DB_PRODUCT, data);
  }
  static deleteDistributor(id) {
    return ApiClient.delete(AdminRoutes.DELETE_DISTRIBUTOR(id));
  }
  static getUserProducts(params) {
    return ApiClient.get(AdminRoutes.GET_USER_PRODUCTS, params);
  }
  static saveSupplier(data) {
    return ApiClient.put(AdminRoutes.SAVE_SUPPLIER, data);
  }
  static getCategories() {
    return ApiClient.get(AdminRoutes.GET_CATEGORIES);
  }
  static saveCategories(data) {
    return ApiClient.post(AdminRoutes.SAVE_CATEGORIES, data);
  }
  static getUsers(data) {
    return ApiClient.get(AdminRoutes.GET_USERS, data);
  }
  static getUserRoles(data) {
    return ApiClient.get(AdminRoutes.GET_USER_ROLES, data);
  }
  static addUser(data) {
    return ApiClient.post(AdminRoutes.ADD_USER, data);
  }
  static putUserData(id, data) {
    return ApiClient.put(AdminRoutes.USER_DATA(id), data);
  }
  static getUser(id, params) {
    return ApiClient.get(AdminRoutes.USER_DATA(id), params);
  }
  static addCategory(data) {
    return ApiClient.post(AdminRoutes.ADD_CATEGORY, data);
  }
  static saveCategory(data) {
    return ApiClient.post(AdminRoutes.SAVE_CATEGORY, data);
  }
  static addUserProductToDb(id) {
    return ApiClient.post(AdminRoutes.USER_PRODUCT_TO_DB, { id });
  }
  static extendValuationDate(uid, extend_to) {
    return ApiClient.put(AdminRoutes.VALUATION_EXTEND, { uid, extend_to });
  }
  static addProducer(name) {
    return ApiClient.put(AdminRoutes.ADD_PRODUCER, { name });
  }
  static deleteSupplierUserConnection(data) {
    return ApiClient.post(AdminRoutes.DELETE_SUPPLIER_USER_CONNECTION, data);
  }
  static moveProducerProducts(data) {
    return ApiClient.post(AdminRoutes.MOVE_PRODUCER_PRODUCTS, data);
  }
  static deleteProducer(id) {
    return ApiClient.delete(AdminRoutes.DELETE_PRODUCER(id));
  }
  static extendUserValuationDate(uid, extend_to) {
    return ApiClient.put(AdminRoutes.USER_VALUATION_EXTEND, { uid, extend_to });
  }
  static moveList(data) {
    return ApiClient.post(AdminRoutes.MOVE_LIST, data);
  }
  static moveValuation(data) {
    return ApiClient.post(AdminRoutes.MOVE_VALUATION, data);
  }
  static remindDistributorValuation(uid) {
    return ApiClient.post(AdminRoutes.REMIND_DISTRIBUTOR_VALUATION, { uid });
  }
  static getCompanies(params, signal) {
    return ApiClient.get(AdminRoutes.GET_COMPANIES, params, { signal });
  }
  static getCompaniesAsTree(params, signal) {
    return ApiClient.get(AdminRoutes.GET_COMPANIES_AS_TREE, params, { signal });
  }

  static moveCompany(data, signal) {
    return ApiClient.post(AdminRoutes.MOVE_COMPANY, data, { signal });
  }
  static getCompanyDetails(id, signal) {
    return ApiClient.get(AdminRoutes.COMPANY_DETAILS(id), {
      signal
    });
  }
  static setCompanyDetails(id, data) {
    return ApiClient.post(AdminRoutes.COMPANY_DETAILS(id), data);
  }
  static getCompanyMembers(params, signal) {
    return ApiClient.get(AdminRoutes.GET_COMPANY_MEMBERS, params, signal);
  }
  static updateShippingDetails(data) {
    return ApiClient.post(AdminRoutes.ADD_SHIPPING_ADDRESS, data);
  }
  static deleteShippingDetails(id) {
    return ApiClient.delete(AdminRoutes.DELETE_SHIPPING_ADDRESS(id));
  }
  static deleteCompanyMember(data) {
    return ApiClient.post(AdminRoutes.DELETE_COMPANY_MEMBER, data);
  }
  static inviteUserToCompany(data, signal) {
    return ApiClient.post(AdminRoutes.INVITE_USER_TO_COMPANY, data, { signal });
  }
  static cancelInvitation(data) {
    return ApiClient.post(AdminRoutes.REMOVE_USER_INVITE_TO_COMPANY, data);
  }
  static getUserToken(user_id) {
    return ApiClient.get(AdminRoutes.GET_USER_TOKEN, { user_id });
  }
  static deleteCategory(id) {
    return ApiClient.post(AdminRoutes.DELETE_CATEGORY, { id });
  }
  static moveCategoryProducts(data) {
    return ApiClient.post(AdminRoutes.MOVE_CATEGORY_PRODUCTS, data);
  }
  static hideCategory(category_id) {
    return ApiClient.post(AdminRoutes.HIDE_CATEGORY, { category_id });
  }
  static showCategory(category_id) {
    return ApiClient.post(AdminRoutes.SHOW_CATEGORY, { category_id });
  }
  static deactivateUser(user_id) {
    return ApiClient.post(AdminRoutes.DEACTIVATE_USER, { user_id });
  }
  static activateUser(user_id) {
    return ApiClient.post(AdminRoutes.ACTIVATE_USER, { user_id });
  }
  static editListProduct(data) {
    return ApiClient.post(AdminRoutes.EDIT_PRODUCT_PRICE, data);
  }
  static getOrderApprovals(params, signal) {
    return ApiClient.get(AdminRoutes.GET_ORDER_APPROVALS, params, { signal });
  }
  static changeSupplierEmail(data) {
    return ApiClient.post(AdminRoutes.CHANGE_SUPPLIER_EMAIL, data);
  }
}

export class WarehouseApi {
  static getWarehouseData(data) {
    return ApiClient.get(WarehouseRoutes.GET_WAREHOUSE_DATA, data);
  }
  static addProduct(data) {
    return ApiClient.put(WarehouseRoutes.ADD_PRODUCT, data);
  }
  static deleteProduct(data) {
    return ApiClient.delete(WarehouseRoutes.DELETE_PRODUCT(data));
  }
  static changeProductState(data) {
    return ApiClient.post(WarehouseRoutes.CHANGE_PRODUCT_STATE, data);
  }
  static getWarehouseHistory(params) {
    return ApiClient.get(WarehouseRoutes.GET_WAREHOUSE_HISTORY, params);
  }
}

export class StatisticsApi {
  static get() {
    return ApiClient.get(StatisticsRoutes.GET_STATISTICS);
  }
  static getProductsData(params, signal) {
    return ApiClient.get(StatisticsRoutes.PRODUCTS_DATA, params, { signal });
  }
  static getExpensesMonthly(params) {
    return ApiClient.get(StatisticsRoutes.GET_EXPENSES_MONTHLY, params);
  }
  static getSavingsMonthly(params) {
    return ApiClient.get(StatisticsRoutes.GET_SAVINGS_MONTHLY, params);
  }
  static getProductsRealisationBySupplier(params) {
    return ApiClient.get(StatisticsRoutes.GET_PRODUCTS_REALISATION_BY_SUPPLIER, params);
  }
  static getProductOrders(params) {
    return ApiClient.get(StatisticsRoutes.GET_PRODUCT_ORDERS(params.id), params);
  }
  static getAvgActionsTimesByOrder(params) {
    return ApiClient.get(StatisticsRoutes.GET_AVG_ACTIONS_TIMES_BY_ORDER, params);
  }
  static getExpensesByCategory(params) {
    return ApiClient.get(StatisticsRoutes.GET_EXPENSES_BY_CATEGORY, params);
  }
  static getActionsTimesForOrder(params) {
    return ApiClient.get(StatisticsRoutes.GET_ACTIONS_TIMES_FOR_ORDER, params);
  }
  static exportExpensesMonthly(params) {
    return ApiClient.get(StatisticsRoutes.EXPORT_EXPENSES_MONTHLY, params);
  }
  static exportExpensesByCategory(params) {
    return ApiClient.get(StatisticsRoutes.EXPORT_EXPENSES_BY_CATEGORY, params);
  }
  static exportProductsRealisationBySupplier(params) {
    return ApiClient.get(StatisticsRoutes.EXPORT_PRODUCTS_REALISATION_BY_SUPPLIER, params);
  }
  static exportSavingsData(params) {
    return ApiClient.get(StatisticsRoutes.EXPORT_SAVINGS_DATA, params);
  }
  static exportAvgActionsTimesByOrder(params) {
    return ApiClient.get(StatisticsRoutes.EXPORT_AVG_ACTIONS_TIMES_BY_ORDER, params);
  }
  static exportActionsTimesByOrder(params) {
    return ApiClient.get(StatisticsRoutes.EXPORT_ACTIONS_TIMES_BY_ORDER, params);
  }
  static exportOrdersData(params) {
    return ApiClient.get(StatisticsRoutes.EXPORT_ORDERS_DATA, params);
  }
  static getProductsRealisationBySupplierMonthly(params) {
    return ApiClient.get(StatisticsRoutes.GET_PRODUCTS_REALISATION_BY_SUPPLIER_MONTHLY, params);
  }
  static getProductsRealisationBySupplierForOrderUid(order_uid) {
    return ApiClient.get(StatisticsRoutes.GET_PRODUCTS_REALISATION_BY_SUPPLIER_FOR_ORDER_UID, { order_uid });
  }
  static getTopProducts(params) {
    return ApiClient.get(StatisticsRoutes.GET_TOP_PRODUCTS, params);
  }
  static exportTopProducts(params) {
    return ApiClient.get(StatisticsRoutes.EXPORT_TOP_PRODUCTS, params);
  }
}

export class PermissionsApi {
  static getAvailable() {
    return ApiClient.get(PermissionsRoutes.GET_AVAILABLE);
  }
  static grantPermission(data) {
    return ApiClient.post(PermissionsRoutes.GRANT_PERMISSION, data);
  }
  static revokePermission(data) {
    return ApiClient.post(PermissionsRoutes.REVOKE_PERMISSION, data);
  }
}

export class LandingApi {
  static registerToNewsletter(params) {
    return ApiClient.get(LandingRoutes.SEND_EMAIL, params);
  }
  static contactUs(data) {
    return ApiClient.post(LandingRoutes.CONTACT_FORM, data);
  }
}

export class ArticlesApi {
  static getArticles(data) {
    return PublicApiClient.get(ArticlesRoutes.GET_ARTICLES, data);
  }
  static getCategories(data) {
    return PublicApiClient.get(ArticlesRoutes.GET_CATEGORIES, data);
  }
  static getPrivateArticles() {
    return PublicApiClient.get(ArticlesRoutes.GET_PRIVATE_ARTICLES);
  }
  static getPrivateArticlePage(id) {
    return PublicApiClient.get(ArticlesRoutes.GET_PRIVATE_ARTICLE_PAGE(id));
  }
}
